import type { Projection } from '~/calculations/projection'
import { projectGoal } from '~/calculations/projection'
import { calculateWeightTrend } from '~/calculations/weightTrends'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { getActiveGoal } from '~/storage/repositories/goalRepository'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { ok, type Result } from '~/utils/result'
import { getTrendObservations, todayFor } from './weightService'

/**
 * The impure boundary of the projection (D-068).
 *
 * Everything that reads storage or the clock happens here; `projectGoal` then
 * runs on a plain value, so the same inputs project the same weeks forever and
 * the whole thing is fixture-testable without a database.
 *
 * **Two conditions are enforced here rather than inside the calculation**, and
 * both are D-036's rather than D-068's:
 *
 *   - the anchor is the current window's seven-day average, and is withheld
 *     when that window has none. A projection built on a single weigh-in would
 *     swing by weeks on one salty dinner;
 *   - the observed pace is passed only when the trend supplied a comparison,
 *     which happens only when **both** windows are sufficient.
 *
 * Doing it here keeps the calculation from re-deriving a sufficiency rule that
 * already has one home.
 */
export async function getProjection(
  db: SolaDatabase,
  now: Date,
): Promise<Result<Projection, StorageError>> {
  const reference = todayFor(now)

  const observations = await getTrendObservations(db, reference)
  if (!observations.ok) return observations

  const phase = await getActivePhase(db)
  if (!phase.ok) return phase

  const goal = await getActiveGoal(db)
  if (!goal.ok) return goal

  const trend = calculateWeightTrend(observations.value, reference)
  const activePhase = phase.value
  const activeGoal = goal.value

  return ok(
    projectGoal({
      anchorKg: trend.current.averageKg,
      targetWeightMinKg: activeGoal?.targetWeightMinKg,
      targetWeightMaxKg: activeGoal?.targetWeightMaxKg,
      phaseRange:
        activePhase === undefined
          ? undefined
          : {
              minPercent: activePhase.targetWeeklyChangeMinPercent,
              maxPercent: activePhase.targetWeeklyChangeMaxPercent,
            },
      /* Reality, not plan. Present only when D-036 allowed a comparison at all —
         the calculation trusts this and does not second-guess it. */
      observedWeeklyRatePercent: trend.comparison?.percentChange,
      reference,
    }),
  )
}
